import {useRef} from 'react';
import type {RefObject} from 'react';

/**
 * Mirrors a value into a ref that always holds the latest render's value.
 *
 * Use when an event listener registered outside React's render cycle (e.g. a
 * `pointermove` listener on `window`) needs to read the most recent prop or
 * state value without re-attaching when the value changes.
 *
 * The ref is **read-only** to callers — the value is owned by this hook.
 *
 * Note: assignment happens during render, which means concurrent rendering
 * could in theory read mid-update. For the editor's drag/resize use case the
 * race is benign: the next pointer event always reads after the render commits.
 */
export function useLatestRef<T>(value: T): RefObject<T> {
  const ref = useRef<T>(value);
  ref.current = value;
  return ref;
}
